import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/use-toast';
import { useRestaurant } from '../../context/RestaurantContext';
import { ChevronLeft, Printer, Download } from 'lucide-react';
import { getReport } from '../../services/liquorTrackingService';

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const STATUS_STYLES = {
  matched: 'bg-slate-100 text-slate-600',
  over: 'bg-emerald-100 text-emerald-700',
  short: 'bg-red-100 text-red-700',
  not_counted: 'bg-amber-100 text-amber-700',
};
const STATUS_LABEL = { matched: 'Matched', over: 'Over', short: 'Short', not_counted: 'Not counted' };

export default function StocktakeReport() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId, stocktakeId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId || !stocktakeId) return;
    setLoading(true);
    try {
      setReport(await getReport(restaurantId, locationId, stocktakeId));
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load report', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, stocktakeId, toast]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!report) return;
    const head = ['Liquor', 'Brand', 'Bottle size (ml)', 'Cost/bottle', 'System (btl)', 'Counted (btl)', 'Variance (btl)', 'System value', 'Counted value', 'Variance value', 'Status'];
    const rows = report.lines.map((l) => [
      l.name, l.brand, l.bottle_size_ml, l.cost_per_bottle,
      l.system_bottles_equiv, l.counted_bottles_equiv, l.variance_bottles,
      l.system_value, l.counted_value, l.variance_value, STATUS_LABEL[l.status] || l.status,
    ]);
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [head, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liquor-stocktake-${stocktakeId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Loading report…</div>;
  if (!report) return <div className="p-6 text-center text-muted-foreground">Report not found.</div>;

  const { stocktake: st, totals, lines } = report;

  return (
    <div className="p-6 w-full">
      <div className="print:hidden">
        <button
          onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking`)}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
        >
          <ChevronLeft className="h-4 w-4" /> Liquor Tracking
        </button>
      </div>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
            {st.name || 'Liquor Stocktake'}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {report.location_name} · {st.liquor_set_name || 'All liquor'} · <span className="capitalize">{st.frequency}</span>
            {' · '}counted {fmtDate(st.completed_at || st.started_at)}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-2" /> CSV</Button>
          <Button variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Print</Button>
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <Metric label="Items" value={totals.expected_items} />
        <Metric label="Counted" value={totals.counted_items} />
        <Metric label="Matched" value={totals.matched} />
        <Metric label="Over" value={totals.over} tone="text-emerald-600" />
        <Metric label="Short" value={totals.short} tone="text-red-600" />
        <Metric label="Not counted" value={totals.not_counted} tone="text-amber-600" />
        <Metric label="Variance value" value={money(totals.variance_value)} tone={totals.variance_value < 0 ? 'text-red-600' : 'text-emerald-600'} />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="border rounded-lg overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Liquor</TableHead>
                  <TableHead className="text-right">System</TableHead>
                  <TableHead className="text-right">Counted</TableHead>
                  <TableHead className="text-right">Variance (btl)</TableHead>
                  <TableHead className="text-right">Variance value</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.dish_id}>
                    <TableCell>
                      <div className="font-medium">{l.name}</div>
                      {l.brand && <div className="text-xs text-muted-foreground">{l.brand}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{l.system_bottles_equiv}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.counted ? l.counted_bottles_equiv : '—'}</TableCell>
                    <TableCell className={`text-right tabular-nums ${l.variance_bottles > 0 ? 'text-emerald-600' : l.variance_bottles < 0 ? 'text-red-600' : ''}`}>
                      {l.counted ? `${l.variance_bottles > 0 ? '+' : ''}${l.variance_bottles}` : '—'}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${l.variance_value > 0 ? 'text-emerald-600' : l.variance_value < 0 ? 'text-red-600' : ''}`}>
                      {l.counted ? money(l.variance_value) : '—'}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 text-[11px] rounded-full font-medium ${STATUS_STYLES[l.status] || ''}`}>
                        {STATUS_LABEL[l.status] || l.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div className={`text-2xl font-display font-semibold tabular-nums ${tone || 'text-foreground'}`}>{value}</div>
    </div>
  );
}
